import { useContext, useCallback, useEffect, useRef } from 'react';

import { TELEPOINTER_DATA_SESSION_ID_ATTR } from '@atlaskit/editor-common/collab';

import { useFullPageEditorApi } from '@confluence/full-page-editor-store';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';
import { TEAM_PRESENCE_CLICK_TO_FIND_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';

import type { PresenceContentMode, PresenceContentType } from './presenceTypes';
import { type PresenceParticipant, useActiveTelepointerBySessions } from './presence-store';

type UseScrollToTelepointerArgs = {
	contentMode: PresenceContentMode;
	contentType: PresenceContentType;
	participant: PresenceParticipant;
};

// Returns a boolean which indicates whether the scroll was successful or not.
// Please note that it exits with false if the provided scroll position has not changed.
interface ScrollToTelepointer {
	(): boolean;
}

const getTelepointerEl = (sessionId: string) => {
	return document.querySelector(`[${TELEPOINTER_DATA_SESSION_ID_ATTR}="${sessionId}"]`);
};

export const useScrollToTelepointer = ({
	contentType,
	contentMode,
	participant,
}: UseScrollToTelepointerArgs) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const isServerPlaceholder = useRenderServerPlaceholder(true);
	const [{ fullPageEditorPresetAPI }] = useFullPageEditorApi();
	// TODO: https://product-fabric.atlassian.net/browse/MDS-1320
	const participantSessions = participant?.sessions ?? {};
	const [activeTelepointer] = useActiveTelepointerBySessions(participantSessions);
	const activeTelepointerPos = activeTelepointer?.selection.head;
	const activeSessionId = activeTelepointer?.sessionId;
	const sessionData = participantSessions[activeSessionId];
	const telepointerEl = isServerPlaceholder ? null : getTelepointerEl(activeSessionId);
	// Keep the optional chaning until we have a stricter "PresenceSessionData"
	const activity = sessionData?.activity;

	const isScrollToFeatureEnabled =
		// This feature is only available for live docs (for "editing" users), and pages and blogposts in edit mode
		(((contentType === 'page' || contentType === 'blogpost') && contentMode === 'edit') ||
			(contentMode === 'live' && activity !== 'viewer')) &&
		// Current user won't have the onClick handler for scroll
		!participant?.isCurrentUser;

	const isEligibleToScroll =
		isScrollToFeatureEnabled &&
		// This is to filter out cases like where we have a user but no telepointer data; e.g. no focus on the doc
		activeTelepointerPos !== undefined &&
		activeSessionId !== undefined &&
		// Even if we have telepointer data, that doesn't guarantee the telepointer will be in the DOM.
		// e.g. platform_editor_no_telecursor_on_live_doc_init,
		telepointerEl !== null;

	const observerRef = useRef<IntersectionObserver | null>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const killObserver = () => {
		if (observerRef.current) {
			observerRef.current.disconnect();
			observerRef.current = null;
		}
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	};

	useEffect(() => {
		return () => {
			// Abort ongoing experience in case it's stuck in an unexpected state
			const expState = experienceTracker.getExperienceState(TEAM_PRESENCE_CLICK_TO_FIND_EXPERIENCE);
			if (expState?.attributes?.sessionId === activeSessionId && !expState?.hasStopped) {
				experienceTracker.abort({
					name: TEAM_PRESENCE_CLICK_TO_FIND_EXPERIENCE,
					reason: 'useScrollToTelepointer unmounted',
				});
			}
		};
	}, [activeSessionId, experienceTracker]);

	useEffect(() => {
		return () => {
			killObserver();
		};
	}, []);

	const scrollToTelepointer: ScrollToTelepointer = useCallback(() => {
		experienceTracker.start({
			name: TEAM_PRESENCE_CLICK_TO_FIND_EXPERIENCE,
			attributes: {
				contentType,
				contentMode,
				sessionId: activeSessionId,
				activity,
			},
		});

		try {
			killObserver();
			const scrollToPos = fullPageEditorPresetAPI?.core.actions.scrollToPos;
			const nudgeTelepointer = fullPageEditorPresetAPI?.collabEdit?.commands.nudgeTelepointer;

			if (!isEligibleToScroll || !scrollToPos) {
				experienceTracker.fail({
					name: TEAM_PRESENCE_CLICK_TO_FIND_EXPERIENCE,
					error: new Error('Eligibility checks failed'),
					attributes: {
						isEligibleToScroll,
						isScrollToPosDefined: scrollToPos !== undefined,
					},
				});
				return false;
			}

			const isScrollSuccessful = scrollToPos(Number(activeTelepointerPos), {
				behavior: 'smooth',
				block: 'center',
			});

			if (isScrollSuccessful && nudgeTelepointer && telepointerEl !== null) {
				// We don't run this callback in SSR, so no need to escape it with an isSSR check
				// eslint-disable-next-line no-restricted-syntax
				observerRef.current = new IntersectionObserver(([entry]) => {
					if (entry.isIntersecting) {
						fullPageEditorPresetAPI?.core.actions.execute(nudgeTelepointer(activeSessionId));
						killObserver();
					}
				});
				observerRef.current.observe(telepointerEl);

				// Edge case: Set a timeout to disconnect the observer if intersection doesn't happen
				// 5000ms is an arbitrary time and can be reduced.
				timeoutRef.current = setTimeout(() => {
					killObserver();
				}, 5000);
			}

			if (!isScrollSuccessful) {
				experienceTracker.fail({
					name: TEAM_PRESENCE_CLICK_TO_FIND_EXPERIENCE,
					error: new Error('Scroll is not successful'),
					attributes: {
						isNudgeTelepointerDefined: nudgeTelepointer !== undefined,
						hasValidTelepointerElement: telepointerEl !== null,
					},
				});
			} else {
				experienceTracker.succeed({
					name: TEAM_PRESENCE_CLICK_TO_FIND_EXPERIENCE,
				});
			}

			return isScrollSuccessful;
		} catch (error) {
			experienceTracker.fail({
				name: TEAM_PRESENCE_CLICK_TO_FIND_EXPERIENCE,
				error,
			});
			return false;
		}
	}, [
		activeSessionId,
		activeTelepointerPos,
		activity,
		contentMode,
		contentType,
		fullPageEditorPresetAPI,
		experienceTracker,
		isEligibleToScroll,
		telepointerEl,
	]);

	return { isScrollToFeatureEnabled, isEligibleToScroll, scrollToTelepointer };
};
